import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  awaitingReplyLabel,
  countAwaitingReply,
  resolveInterestLabel,
  todayJst,
  type AppUser,
  type InterestTagConfig,
  type PromoInterestKey,
  type SegmentSpec,
} from '@kawai/shared';
import { Card, Chip, Input } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { useAllUsers } from '../lib/hooks/useAllUsers.js';
import {
  useRichmenuTapStats,
  useRichmenuTapEvents,
} from '../lib/hooks/useRichmenuTapStats.js';
import { useCouponUsageActivity } from '../lib/hooks/useCouponUsageActivity.js';
import type { UserCouponActivity } from '../lib/couponStats.js';
import { useSegmentMasterOptions } from '../lib/hooks/useSegmentMasterOptions.js';
import { useInterestTagConfig } from '../lib/hooks/useInterestTagConfig.js';
import { useSurveys } from '../lib/hooks/useSurveys.js';
import { useLotteryEvent } from '../lib/hooks/useLotteryStatus.js';
import { useCouponHolders } from '../lib/hooks/useCouponHolders.js';
import { lotteryCouponSegmentOptions } from '../lib/couponSegmentOptions.js';
import { usePiiAccessAudit } from '../lib/hooks/usePiiAccessAudit.js';
import { resolveConfigModalClose, shouldOpenConfigModal } from '../lib/returnTo.js';
import { SegmentFilterBar } from '../components/SegmentFilterBar.js';
import { InterestTagConfigModal } from '../components/InterestTagConfigModal.js';
import { RichmenuTapAnalytics } from '../components/RichmenuTapAnalytics.js';
import {
  isBookedCustomer,
  formatBookingName,
  matchesCustomerFilter,
  reactionActionsOf,
  REACTION_ACTION_ORDER,
} from '../lib/customerStats.js';
import { buildUserItemTapMap, summarizeTabUniqueUsers } from '../lib/richmenuTapStats.js';
import { RICHMENU_LAYOUT } from '../lib/richmenuLayout.js';
import {
  filterUsersBySegment,
  isEmptySegment,
  summarizeSegment,
  reactionActionLabel,
  reactionBadgeColorClass,
  RICHMENU_ITEM_LABELS,
  REACTION_ACTION_LABELS,
  INTEREST_STAGE_LABELS,
} from '../lib/segment.js';
import {
  resolveCustomerStage,
  stageDecayNote,
  stageDecayNoteShort,
  STAGE_DECAY_MARK,
} from '../lib/customerStage.js';
import { ROUTES } from '../routes.js';

export { isBookedCustomer };

const EMPTY_SEGMENT: SegmentSpec = {};
const EMPTY_SELECTION: ReadonlySet<string> = new Set();
const EMPTY_COUPON_ACTIVITY: ReadonlyMap<string, UserCouponActivity> = new Map();

/**
 * 「各リッチメニューボタン」フィルターの選択肢（A→B タブのレイアウト順）。
 * item キーは `RICHMENU_LAYOUT`（= promo_events.payload.item）と一致し、ラベルは
 * `RICHMENU_ITEM_LABELS` で解決する。
 */
const RICHMENU_FILTER_ITEMS: readonly string[] = RICHMENU_LAYOUT.flatMap((t) =>
  t.rows.flat().map((c) => c.item),
);

/** フィルターチップの class（S-134 v2: 選択中 = ブランド面 / 非選択 = 白面。枠線は使わない）。 */
function filterChipClass(active: boolean): string {
  return active
    ? 'rounded-lg bg-brand px-3.5 py-1.5 text-label font-medium text-white'
    : 'rounded-lg bg-white px-3.5 py-1.5 text-label text-ink-2 hover:bg-surface-3';
}

/**
 * 顧客一覧 + セグメント絞り込み（LE-00 / promo feature）。
 *
 * - `useAllUsers(tenantId)` で自テナント顧客を取得
 * - SegmentFilterBar で条件指定 → `filterUsersBySegment`（純関数）で client-side 絞り込み
 * - displayName 部分一致検索（case-insensitive / かな揺らぎ非対応）
 * - 行クリックで顧客詳細へ
 */
export function CustomersListPage() {
  const { tenantId } = useAuth();
  const { data, isLoading, error } = useAllUsers(tenantId);
  const { data: masterOptions } = useSegmentMasterOptions(tenantId);
  // M-97: アンケート回答での絞り込み（SegmentFilterBar の条件）に使う定義。
  const { data: surveys } = useSurveys(tenantId);
  const interestConfigQuery = useInterestTagConfig(tenantId);
  const interestConfig = interestConfigQuery.data;
  // M-166: クーポン保有条件（選択肢はルーレットの用途 / 判定材料は coupon_usages の集合）。
  const { data: lotteryEvent } = useLotteryEvent(tenantId);
  const couponOptions = useMemo(() => {
    const opts = lotteryCouponSegmentOptions(lotteryEvent);
    return opts.length > 0 ? opts : undefined;
  }, [lotteryEvent]);
  const queryClient = useQueryClient();
  const richmenuTaps = useRichmenuTapStats(tenantId);
  const richmenuTapEvents = useRichmenuTapEvents(tenantId);
  // M-68: lineUserId → クーポン横断アクティビティ（バッジの MM/DD 前置 + フィルター和集合判定）。
  const couponUsageActivity = useCouponUsageActivity(tenantId);
  const couponActivityMap = couponUsageActivity.data ?? EMPTY_COUPON_ACTIVITY;
  const [segment, setSegment] = useState<SegmentSpec>(EMPTY_SEGMENT);
  // M-166: 判定材料（lineUserId 集合）。読込中は fail-closed で 0 行（過大に見せない）。
  const {
    ctx: couponCtx,
    isLoading: couponLoading,
    error: couponError,
  } = useCouponHolders(tenantId, segment);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(EMPTY_SELECTION);
  const [segmentOpen, setSegmentOpen] = useState(false);
  // M-90: 販促プラン画面の「商品特性を編集 →」から `/?interestConfig=1` で直接開けるようにする
  // （モーダルを 2 画面に生やすと保存後の invalidate 対象が割れるため、deep link を採る）。
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [configOpen, setConfigOpen] = useState(() => shouldOpenConfigModal(searchParams));

  // M-90 追補: 閉じたら「開く前の場所」へ戻す。戻り先が無ければ deep link の query だけ
  // 落として留まる — 残したままだと**閉じた後のリロードでモーダルが復活する**。
  // 遷移・query 更新とも replace で、戻るボタンがモーダル URL に跳ね返らないようにする。
  const closeConfig = useCallback(() => {
    setConfigOpen(false);
    const action = resolveConfigModalClose(searchParams);
    if (action.type === 'navigate') navigate(action.to, { replace: true });
    else if (action.type === 'clearParams')
      setSearchParams(action.search, { replace: true });
  }, [navigate, searchParams, setSearchParams]);

  const all = useMemo(() => data ?? [], [data]);

  // S-74: 顧客 PII 一覧の表示を監査記録（DPA 第7条1項 / 別添①5.4）。
  // データ読込完了（PII が実際に画面に出る段）で 1 回だけ発火する。
  usePiiAccessAudit('customer_list', {
    count: all.length,
    enabled: !isLoading && !error && !!tenantId,
  });

  // userId → タップした item 集合（「各リッチメニューボタン」フィルター用）。
  const userItemTapMap = useMemo(
    () => buildUserItemTapMap(richmenuTapEvents.data ?? []),
    [richmenuTapEvents.data],
  );
  // M-162: タブごとのユニーク人数（「M 人中 K 人がタップ」チップ）。
  const tabUniqueUsers = useMemo(
    () => summarizeTabUniqueUsers(richmenuTapEvents.data ?? [], RICHMENU_LAYOUT),
    [richmenuTapEvents.data],
  );

  // セグメント条件 → 区分フィルター（複数選択 OR）→ 検索 → sort の順に client-side で絞り込む。
  // M-74: minStage の評価時減衰を商材×テナント別に解決するため config（engines）を注入
  //（サーバ配信 runSegmentPush と同じ評価になる）。ロード中 undefined は商材別既定で評価。
  // M-116: 評価時刻は **memo の中で 1 つ作り、絞り込みとバッジの両方に配る**。
  // 別々に `new Date()` すると、この memo は deps に時刻を持たないため
  // 「JST 日跨ぎ後に deps を変えない再描画」で両者が 1 日ぶんズレる — 実測で
  // 「最低ステージ=ステージ3（欲求）で絞った一覧に ステージ3（欲求）↓（＝実効は
  // ステージ2）の行が残る」という自己矛盾が再現した。時刻を共有すれば、
  // memo が stale な間も**行とバッジは必ず同じ判断**になる（deps が変われば両方更新される）。
  const { rows: filtered, evaluatedAt } = useMemo(() => {
    const now = new Date();
    const bySegment = isEmptySegment(segment)
      ? all
      : filterUsersBySegment(all, segment, now, interestConfig ?? undefined, couponCtx);
    const byAudience =
      selected.size === 0
        ? bySegment
        : bySegment.filter((u) =>
            matchesCustomerFilter(
              u,
              selected,
              userItemTapMap.get(u.uid),
              u.lineUserId ? couponActivityMap.get(u.lineUserId) : undefined,
            ),
          );
    const q = search.trim().toLowerCase();
    const bySearch = q
      ? byAudience.filter((u) => u.displayName.toLowerCase().includes(q))
      : byAudience;
    return { rows: sortByLastEvent(bySearch), evaluatedAt: now };
  }, [
    all,
    segment,
    search,
    selected,
    userItemTapMap,
    couponActivityMap,
    interestConfig,
    couponCtx,
  ]);

  // M-147: 未返信の人数は全件ベース（絞り込みに依らない）。日数表示は行と同じ評価時刻で。
  const awaitingReplyCount = useMemo(() => countAwaitingReply(all), [all]);
  // `awaitingReplyLabel` は日付部分しか見ないので JST の日付文字列で足りる
  const evaluatedAtIso = useMemo(() => todayJst(evaluatedAt), [evaluatedAt]);

  const toggleFilter = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!tenantId) {
    return (
      <p className="text-body text-ink-2">
        テナントが特定できません。サインインし直してください。
      </p>
    );
  }
  if (isLoading) return <p className="text-body text-ink-3">読み込み中…</p>;
  if (error) {
    return (
      <p role="alert" className="text-body text-danger-ink">
        顧客一覧の取得に失敗しました: {error.message}
      </p>
    );
  }

  // 凡例は `↓` が実際に出ている時だけ出す（常設すると読まれなくなる）。
  const hasDecayedStage = filtered.some((u) =>
    (Object.keys(u.interestTags ?? {}) as PromoInterestKey[]).some(
      (k) => resolveCustomerStage(u, k, interestConfig, evaluatedAt)?.decayed === true,
    ),
  );

  return (
    <section className="space-y-5">
      <header className="flex items-center justify-between">
        <h2 className="text-h-page font-semibold text-ink">顧客管理</h2>
        <span className="flex items-center gap-2.5 text-body text-ink-2">
          {/* M-147: 未返信の人数（全件ベース / フィルタに依らず常に見える） */}
          {awaitingReplyCount > 0 && (
            <button
              type="button"
              onClick={() => setSelected(new Set(['awaiting_reply']))}
              className="rounded-full bg-warning-tint px-3 py-0.5 text-note font-medium text-warning-ink hover:opacity-80"
              data-testid="awaiting-reply-count"
            >
              未返信 {awaitingReplyCount} 件
            </button>
          )}
          <span>
            {filtered.length} / {all.length} 件
            {couponLoading ? '（クーポン条件を計算中…）' : ''}
            {couponError ? (
              <span className="text-warning-ink">
                （クーポン条件の判定材料を読み込めませんでした — 0
                件は実数ではありません）
              </span>
            ) : null}
          </span>
        </span>
      </header>

      {/* M-161: 分析サマリー（M-159）は左メニューの独立ページ `/analytics` へ。
          興味タグ設定モーダルは M-90 の deep link（`/?interestConfig=1`）の着地先なので残す。 */}

      {/* M-74: config 取得が確定する前にモーダルを開くと、保存時の engines: {} 明示送信が
          既存の engine 上書きを全消去してしまうため、初回取得完了（null = 未作成含む）まで
          開かない。判定は isSuccess ではなく data !== undefined — バックグラウンド refetch の
          一時失敗で status が error になっても data は保持されるため、編集中のモーダルが
          unmount されて入力が消えるのを防ぐ（初回未完了時は data undefined でガードは有効）。 */}
      {configOpen &&
        (interestConfigQuery.data !== undefined ? (
          <InterestTagConfigModal
            config={interestConfig ?? null}
            menuOptions={masterOptions.menuOptions}
            categoryOptions={masterOptions.categoryOptions}
            onClose={closeConfig}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['promo', 'interestTagConfig'] });
              queryClient.invalidateQueries({ queryKey: ['promo', 'allUsers'] });
            }}
          />
        ) : (
          <p role="alert" className="text-body text-danger-ink">
            興味タグ設定を読み込めていません（読込中または取得失敗）。
            {interestConfigQuery.isError && (
              <button
                type="button"
                onClick={() => void interestConfigQuery.refetch()}
                className="ml-2 rounded-lg bg-surface-2 px-3 py-1 text-label font-medium text-danger-ink hover:bg-surface-3"
              >
                再読み込み
              </button>
            )}
          </p>
        ))}

      <RichmenuTapAnalytics
        summary={richmenuTaps.data}
        isLoading={richmenuTaps.isLoading}
        error={richmenuTaps.error}
        tabUniqueUsers={tabUniqueUsers}
      />

      {/* セグメント条件はコンパクトに畳んでおき、必要時のみ展開する。 */}
      <Card shadow="none" border="none" padding="none" className="rounded-xl">
        <button
          type="button"
          aria-expanded={segmentOpen}
          aria-controls="segment-filter-panel"
          onClick={() => setSegmentOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 px-[30px] py-4 text-left text-body"
        >
          <span
            aria-hidden="true"
            className={`text-ink-3 transition-transform ${segmentOpen ? 'rotate-90' : ''}`}
          >
            ▸
          </span>
          <span className="text-em font-semibold text-ink">
            {isEmptySegment(segment) ? 'セグメントで絞り込む' : 'セグメント条件'}
          </span>
          <span className="truncate text-label text-ink-3">
            {summarizeSegment(segment)}
          </span>
        </button>
        {segmentOpen && (
          <div id="segment-filter-panel" className="px-[30px] pb-[26px] pt-1">
            <SegmentFilterBar
              value={segment}
              onChange={setSegment}
              menuOptions={masterOptions.menuOptions}
              categoryOptions={masterOptions.categoryOptions}
              {...(surveys ? { surveys } : {})}
              {...(couponOptions ? { couponOptions } : {})}
            />
          </div>
        )}
      </Card>

      {/* 区分 + 各リッチメニューボタンの複数選択フィルター（OR）。「すべて」で全解除。 */}
      <div role="group" aria-label="顧客の絞り込み" className="space-y-2.5">
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            aria-pressed={selected.size === 0}
            onClick={() => setSelected(EMPTY_SELECTION)}
            className={filterChipClass(selected.size === 0)}
          >
            すべて
          </button>
          <button
            type="button"
            aria-pressed={selected.has('booked')}
            onClick={() => toggleFilter('booked')}
            className={filterChipClass(selected.has('booked'))}
          >
            予約客
          </button>
          <button
            type="button"
            aria-pressed={selected.has('awaiting_reply')}
            onClick={() => toggleFilter('awaiting_reply')}
            className={filterChipClass(selected.has('awaiting_reply'))}
          >
            未返信
          </button>
        </div>
        <div
          role="group"
          aria-label="行動アクションで絞り込む"
          className="flex flex-wrap items-center gap-1.5"
        >
          <span aria-hidden="true" className="mr-1.5 text-label font-medium text-ink-2">
            行動アクション
          </span>
          {REACTION_ACTION_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={selected.has(key)}
              onClick={() => toggleFilter(key)}
              className={filterChipClass(selected.has(key))}
            >
              {REACTION_ACTION_LABELS[key]}
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label="リッチメニュー反応で絞り込む"
          className="flex flex-wrap items-center gap-1.5"
        >
          <span aria-hidden="true" className="mr-1.5 text-label font-medium text-ink-2">
            リッチメニュー
          </span>
          {RICHMENU_FILTER_ITEMS.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={selected.has(item)}
              onClick={() => toggleFilter(item)}
              className={filterChipClass(selected.has(item))}
            >
              {RICHMENU_ITEM_LABELS[item] ?? item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block">
          <span className="sr-only">表示名で検索</span>
          <Input
            type="search"
            aria-label="表示名で検索"
            placeholder="表示名で検索（ひらがな/カタカナ区別あり）"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {/* M-116: `↓` の意味は `title`（hover 専用）と読み上げ用テキストにしかなく、
          晴眼のキーボード利用者とタッチ利用者に届かない。常時可視の凡例で塞ぐ。 */}
      {hasDecayedStage && (
        <p
          data-testid="stage-decay-legend"
          className="rounded-lg bg-warning-tint px-[18px] py-3 text-label text-ink-2"
        >
          <span className="font-semibold text-warning-ink">{STAGE_DECAY_MARK}</span> …
          反応が途切れており、
          <strong className="font-semibold">配信のしぼり込みでは 1 つ下のステージ</strong>
          として扱われます（顧客詳細を開くと、その商材が配信でどう扱われるか文章で出ます）。
        </p>
      )}

      {all.length === 0 ? (
        <p className="text-body text-ink-2">
          顧客が登録されていません。LIFF
          経由でサインインしたユーザーのみここに表示されます。
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-body text-ink-2">条件に一致する顧客がいません。</p>
      ) : (
        <Card
          as="ul"
          shadow="none"
          border="none"
          padding="none"
          className="overflow-hidden rounded-xl"
        >
          {filtered.map((u) => {
            const realName = formatBookingName(u);
            const awaitingLabel = awaitingReplyLabel(u, evaluatedAtIso);
            // M-68: バッジの MM/DD 前置と有無判定（和集合）に使うクーポン横断アクティビティ。
            const coupon = u.lineUserId ? couponActivityMap.get(u.lineUserId) : undefined;
            return (
              <li
                key={u.uid}
                data-testid="customer-row"
                data-uid={u.uid}
                className="even:bg-surface-2"
              >
                <Link
                  to={ROUTES.customerDetail(u.uid)}
                  className="flex items-center gap-3.5 px-[30px] py-3.5 hover:bg-surface-3"
                >
                  {u.pictureUrl ? (
                    <img
                      src={u.pictureUrl}
                      alt={u.displayName}
                      referrerPolicy="no-referrer"
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-surface-3" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-em font-semibold text-ink">
                        {u.displayName}
                      </span>
                      {realName && (
                        <span className="shrink-0 text-label text-ink-3">
                          【{realName}】
                        </span>
                      )}
                      {isBookedCustomer(u) && (
                        <Chip tone="success" className="shrink-0">
                          予約客
                        </Chip>
                      )}
                      {/* M-87: LINE ブロック / 友だち解除済み。セグメント配信の対象から
                          無条件で外れる（matchesSegment）ため、母数が減った理由が
                          一覧で説明できるようにバッジで明示する。 */}
                      {u.lineBlocked === true && (
                        <Chip tone="neutral" className="shrink-0">
                          ブロック中
                        </Chip>
                      )}
                      {/* M-147: 顧客の LINE に返事をしていない（users.lastInboundAt > lastReplyAt）。
                          自動配信では消えない（返信と数える経路は shared の LINE_REPLY_ORIGINS）。 */}
                      {awaitingLabel && (
                        <Chip
                          tone="warning"
                          className="shrink-0"
                          data-testid="awaiting-reply-badge"
                        >
                          {awaitingLabel}
                        </Chip>
                      )}
                      {reactionActionsOf(u, coupon)
                        .filter((key) => key !== 'none')
                        .map((key) => (
                          <span
                            key={key}
                            className={`shrink-0 rounded-full px-2 py-0.5 text-note font-medium ${reactionBadgeColorClass(key)}`}
                          >
                            {reactionActionLabel(key, coupon)}
                          </span>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3.5 text-label text-ink-3">
                      {u.washpassStatus && u.washpassStatus !== 'none' && (
                        <span>WashPass: {u.washpassStatus}</span>
                      )}
                      {u.lastEventAt && (
                        <span>最終行動: {u.lastEventAt.slice(0, 10)}</span>
                      )}
                      {u.lastBookingAt && (
                        // lastBookingAt は最終予約の来店“予定”日（予約作成時に denormalize / M-37）。
                        // 実来店の確定値ではないため「最終来店」ではなく「最終予約日」と表示する（M-61）。
                        <span>最終予約日: {u.lastBookingAt.slice(0, 10)}</span>
                      )}
                    </div>
                    {renderInterestTags(u, interestConfig, evaluatedAt)}
                  </div>
                </Link>
              </li>
            );
          })}
        </Card>
      )}
    </section>
  );
}

/**
 * 興味タグを小さなバッジ列で表示（保有キーのみ / M-62: ラベルは config 優先解決）。
 *
 * M-116: 同じバッジにステージを併記する。**バッジ列は増やさない** — 行にはすでに
 * 予約客 / ブロック中 / 行動アクションが並んでおり、商材ごとのステージを別バッジにすると
 * 5 商材で確実に溢れる。商材名とステージは 1:1 なので同じチップに入るのが自然でもある。
 *
 * ⚠ **出すのは保存値（`stored`）で、減衰後の実効値ではない。** 実効値を出すと
 * 「ステージは時間では下がらない / 下がるのは配信の判定だけ」という説明ページの主題が
 * 画面から消える。ただし**同じ画面の「最低ステージ」は実効値で判定している**ため、
 * 保存値だけを出すと「ステージ3（欲求）と出ているのに ステージ3 以上で消える行」が
 * 説明できなくなる。そこで減衰しているものには `↓` を付け、読み上げ用に実効値を添える。
 */
function renderInterestTags(
  u: AppUser,
  config: InterestTagConfig | null | undefined,
  now: Date,
) {
  const tags = u.interestTags;
  if (!tags) return null;
  const keys = Object.keys(tags) as PromoInterestKey[];
  if (keys.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {keys.map((k) => {
        const stage = resolveCustomerStage(u, k, config, now);
        return (
          <span
            key={k}
            data-testid="interest-tag"
            data-interest-key={k}
            data-stage={stage?.stored ?? ''}
            data-effective-stage={stage?.effective ?? ''}
            title={stage?.decayed ? stageDecayNote(stage.effective) : undefined}
            className="rounded-full bg-brand-tint px-2 py-0.5 text-note font-medium text-brand-ink"
          >
            {resolveInterestLabel(k, config ?? undefined)}
            {stage && (
              <>
                {/* aria-hidden の記号だけを挟むと、Chrome は前後を空白なしで連結して
                    「給油ステージ3（欲求）」と読み上げる（Firefox は空白を入れるので
                    ブラウザ間で割れ、jsdom ベースのテストでは検出できない）。
                    実テキストの空白で挟んで名前計算を安定させる。 */}{' '}
                <span aria-hidden="true">/</span> {INTEREST_STAGE_LABELS[stage.stored]}
                {stage.decayed && (
                  <>
                    <span
                      aria-hidden="true"
                      className="ml-0.5 font-semibold text-warning-ink"
                    >
                      {STAGE_DECAY_MARK}
                    </span>
                    {/* リンクのアクセシブル名は行のテキスト全部の連結になる。商材ごとに
                        長文を足すと 5 商材で名前が 250 文字を超えて流し読みができなくなるため、
                        一覧では短く留め、全文は顧客詳細に置く。 */}
                    <span className="sr-only">
                      {stageDecayNoteShort(stage.effective)}
                    </span>
                  </>
                )}
              </>
            )}
          </span>
        );
      })}
    </div>
  );
}

/** lastEventAt desc（未設定は末尾）→ displayName asc fallback（pure / 非破壊）。 */
function sortByLastEvent(users: AppUser[]): AppUser[] {
  return users.slice().sort((a, b) => {
    const ta = a.lastEventAt ?? '';
    const tb = b.lastEventAt ?? '';
    if (ta && tb && ta !== tb) return ta < tb ? 1 : -1; // desc
    if (ta && !tb) return -1;
    if (!ta && tb) return 1;
    return a.displayName.localeCompare(b.displayName, 'ja');
  });
}
